import { clsx } from '@alexis/helpers/clsx';
import { translateNumber } from '@alexis/helpers/translation';
import { useFlightPLS } from '@pages/FlightSearchResult/contexts/FlightPerLegSearchContext/FlightPerLegSearchContext';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchFilters from '@pages/FlightSearchResult/hooks/useFlightSearchFilters';
import useFlightSearchPageParams from '@pages/FlightSearchResult/hooks/useFlightSearchPageParams';
import { SliderFiltersUniqueForEachLeg } from '@pages/FlightSearchResult/types';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import Slider from '@components/Slider';

import {
  getMinMaxRangeOfASliderFilterUniqueForEachLeg,
  getRange,
  getSelectedRangeOfASliderFilterUniqueForEachLeg,
  getSelectedRangeOfTransitDurationFilter,
  getTransitDurationMinMaxForLegsWithFilterApplied,
  renderLegLabel,
} from '@helpers/flight';

import useFlightSearchResultsDatadog from '@hooks/useFlightSearchResultsDatadog';

import { getIsRtlState, getLocaleState, getTranslationsState } from '@redux/selectors';

import { FlightSearchResultSearchParams } from '@wegoTypes/flights/flightSearchParams';

import styles from '../FlightFilterCategoryContent.module.scss';

type DurationFilterProps = {
  className?: string;
  genzoTrackApplyFilterActionEvent: any;
  genzoTrackApplyFilterSearchView: () => void;
};

const DurationFilter: React.FC<DurationFilterProps> = ({
  className,
  genzoTrackApplyFilterActionEvent,
  genzoTrackApplyFilterSearchView,
}) => {
  const {
    activeLeg,
    departLegFilterOption,
    returnLegFilterOption,
    returnDurationsFilter: _returnDurationsFilter,
    departDurationsFilter: _departDurationsFilter,
    stopoverDurationsFilter: _stopoverDurationsFilter,
    changeMultiCityRangeFilter,
  } = useFlightSearchFilters();
  const { isPerLegSearch } = useFlightPLS();

  const { legFilterOptions, stopoverDurationFilterOptions: _stopoverDurationFilterOptions } =
    useFlightSearchResults();

  const { isRoundTrip, isMultiCity } = useFlightSearchPageParams();

  const stopoverDurationFilterOptions = isMultiCity
    ? departLegFilterOption?.leg?.stopoverDurations
    : _stopoverDurationFilterOptions;

  const minDepartDuration = legFilterOptions[activeLeg]?.leg?.durations.min || 0;
  const maxDepartDuration = legFilterOptions[activeLeg]?.leg?.durations.max || 0;
  const minReturnDuration = returnLegFilterOption?.leg?.durations.min || 0;
  const maxReturnDuration = returnLegFilterOption?.leg?.durations.max || 0;

  const [searchParams] = useSearchParams();
  const departDurationParam = searchParams.get(FlightSearchResultSearchParams.DepartDuration);
  const returnDurationParam = searchParams.get(FlightSearchResultSearchParams.ReturnDuration);

  const departDurationsFilter = useMemo(
    () =>
      legFilterOptions.map((legFilter, index) => ({
        leg: index,
        values: _departDurationsFilter.find(({ leg }) => leg === index)?.values ?? [
          legFilter.leg?.durations.min || 0,
          legFilter.leg?.durations.max || 0,
        ],
      })),
    [_departDurationsFilter, legFilterOptions],
  );

  const returnDurationsFilter = useMemo(
    () =>
      _returnDurationsFilter.length
        ? _returnDurationsFilter
        : [minReturnDuration || 0, maxReturnDuration || 0],
    [_returnDurationsFilter, maxReturnDuration, minReturnDuration],
  );

  const stopoverDurationsFilter = useMemo(
    () =>
      legFilterOptions.map((legFilter, index) => ({
        leg: index,
        values:
          _stopoverDurationsFilter.find(({ leg }) => leg === index)?.values ??
          (isMultiCity
            ? [legFilter.leg?.stopoverDurations.min || 0, legFilter.leg?.stopoverDurations.max || 0]
            : [stopoverDurationFilterOptions?.min || 0, stopoverDurationFilterOptions?.max || 0]),
      })),
    [
      _stopoverDurationsFilter,
      isMultiCity,
      isPerLegSearch,
      legFilterOptions,
      stopoverDurationFilterOptions?.max,
      stopoverDurationFilterOptions?.min,
    ],
  );

  const [newSelectedReturnDuration, setNewSelectedReturnDuration] = useState(returnDurationsFilter);
  const [newSelectedDepartDuration, setNewSelectedDepartDuration] = useState(departDurationsFilter);
  const [newSelectedStopoverDuration, setNewSelectedStopoverDuration] =
    useState(stopoverDurationsFilter);

  useEffect(() => {
    setNewSelectedDepartDuration(departDurationsFilter);
  }, [departDurationsFilter]);

  useEffect(() => {
    setNewSelectedReturnDuration(returnDurationsFilter);
  }, [returnDurationsFilter]);

  useEffect(() => {
    setNewSelectedStopoverDuration(stopoverDurationsFilter);
  }, [stopoverDurationsFilter]);

  const isRtl = useSelector(getIsRtlState);
  const translations = useSelector(getTranslationsState);
  const locale = useSelector(getLocaleState);

  const { recordChangeFilterAction } = useFlightSearchResultsDatadog();

  const durationDisplayFormat = (minutes: number) => {
    const hours = Math.floor(minutes / 60)
      .toString()
      .padStart(2, '0');
    const minute = (minutes % 60).toString().padStart(2, '0');

    return translateNumber(`${hours}h ${minute}m`, locale === 'fa');
  };

  const handleSelectedDepartDurationChanged = (departDuration: number[]) => {
    const newFilter = changeMultiCityRangeFilter({
      range: departDuration,
      min: minDepartDuration,
      max: maxDepartDuration,
      searchParamKey: FlightSearchResultSearchParams.DepartDuration,
      activeLeg,
    });

    if (!isMultiCity && !isPerLegSearch) {
      genzoTrackApplyFilterSearchView();
    }

    genzoTrackApplyFilterActionEvent('filter', 'applied', {
      totalduration_min_max: getMinMaxRangeOfASliderFilterUniqueForEachLeg(
        newFilter.join(':'),
        returnDurationParam,
        SliderFiltersUniqueForEachLeg.TotalDurationFilter,
        isRoundTrip,
        legFilterOptions,
        isPerLegSearch,
        activeLeg,
      ),
      totalduration_selected: getSelectedRangeOfASliderFilterUniqueForEachLeg(
        newFilter.join(':'),
        returnDurationParam,
        isRoundTrip,
        isPerLegSearch,
        activeLeg,
      ),
    });
  };

  const handleSelectedReturnDurationChanged = (returnDuration: number[]) => {
    const newFilter = changeMultiCityRangeFilter({
      activeLeg,
      range: returnDuration,
      min: minReturnDuration,
      max: maxReturnDuration,
      searchParamKey: FlightSearchResultSearchParams.ReturnDuration,
    });

    genzoTrackApplyFilterSearchView();

    genzoTrackApplyFilterActionEvent('filter', 'applied', {
      totalduration_min_max: getMinMaxRangeOfASliderFilterUniqueForEachLeg(
        departDurationParam,
        newFilter.join(':'),
        SliderFiltersUniqueForEachLeg.TotalDurationFilter,
        isRoundTrip,
        legFilterOptions,
      ),
      totalduration_selected: getSelectedRangeOfASliderFilterUniqueForEachLeg(
        departDurationParam,
        newFilter.join(':'),
        isRoundTrip,
      ),
    });
  };

  const handleSelectedStopoverDurationChanged = (stopoverDuration: number[]) => {
    const newFilter = changeMultiCityRangeFilter({
      activeLeg,
      min: stopoverDurationFilterOptions?.min,
      max: stopoverDurationFilterOptions?.max,
      range: stopoverDuration,
      searchParamKey: FlightSearchResultSearchParams.StopoverDuration,
    });

    if (!isMultiCity && !isPerLegSearch) {
      genzoTrackApplyFilterSearchView();
    }

    if (isPerLegSearch) {
      genzoTrackApplyFilterActionEvent('filter', 'applied', {
        transitduration_min_max: newFilter[activeLeg]
          ? [stopoverDurationFilterOptions?.min, stopoverDurationFilterOptions?.max]
          : undefined,
        transitduration_selected: newFilter[activeLeg] ? newFilter[activeLeg] : undefined,
      });
    } else {
      genzoTrackApplyFilterActionEvent('filter', 'applied', {
        transitduration_min_max: getTransitDurationMinMaxForLegsWithFilterApplied(
          newFilter.join(':'),
          isRoundTrip,
          legFilterOptions,
        ),
        transitduration_selected: getSelectedRangeOfTransitDurationFilter(
          newFilter.join(':'),
          isRoundTrip,
        ),
      });
    }
  };

  const handleActiveLegDepartDurationChanged = (value: number[]) => {
    const updatedDuration = departDurationsFilter.map((item) =>
      item.leg === activeLeg ? { ...item, values: value } : item,
    );

    setNewSelectedDepartDuration(updatedDuration);
  };

  const handleActiveLegStopoverDurationChanged = (value: number[]) => {
    const updatedStopoverDuration = stopoverDurationsFilter.map((item) =>
      item.leg === activeLeg ? { ...item, values: value } : item,
    );

    setNewSelectedStopoverDuration(updatedStopoverDuration);
  };

  const selectedDepartDurationForActiveLeg =
    newSelectedDepartDuration.find((item) => item.leg === activeLeg)?.values || [];

  const selectedStopoverDurationForActiveLeg =
    newSelectedStopoverDuration.find((item) => item.leg === activeLeg)?.values || [];

  if (isRoundTrip && !isPerLegSearch)
    return (
      <div
        className={clsx(
          styles.categoryContent,
          !!className && styles.inDropdown,
          !!className && styles.durationContainer,
        )}
      >
        {legFilterOptions.map((legFilter, index) => {
          const leg = legFilter.leg!;
          const isDepartLeg = index === 0;

          const selectedDuration = isDepartLeg
            ? selectedDepartDurationForActiveLeg
            : newSelectedReturnDuration;
          const handleDurationChanged = isDepartLeg
            ? handleActiveLegDepartDurationChanged
            : setNewSelectedReturnDuration;

          const handleDurationChangeCommitted = isDepartLeg
            ? handleSelectedDepartDurationChanged
            : handleSelectedReturnDurationChanged;

          return (
            <React.Fragment key={legFilter.name}>
              <div className={styles.itemHeader}>
                {translations.trip_time}{' '}
                <span className={styles.route}>
                  {renderLegLabel({
                    depart: legFilter.departureAirport?.code || legFilter.departureCity?.code || '',
                    arrival: legFilter.arrivalAirport?.code || legFilter.arrivalCity?.code || '',
                    isRtl,
                  })}
                </span>
              </div>
              <div
                className={clsx(styles.sliderContainer)}
                data-testid={`slider-${isDepartLeg ? 'depart' : 'return'}`}
              >
                <div className={styles.labelRange}>
                  <div className={styles.label}>{durationDisplayFormat(selectedDuration[0])}</div>
                  <div className={styles.label}>{durationDisplayFormat(selectedDuration[1])}</div>
                </div>

                <Slider
                  dataPw='flightFilter_duration'
                  isRtl={isRtl}
                  max={leg.durations.max}
                  min={leg.durations.min}
                  step={5}
                  value={selectedDuration}
                  onChange={(values, activeThumb) => {
                    const durationRange = getRange({
                      activeThumb,
                      values,
                      max: leg.durations.max,
                      min: leg.durations.min,
                      minimumDistance: 5,
                    });

                    if (durationRange) handleDurationChanged(durationRange);
                  }}
                  onChangeCommitted={(values, activeThumb) => {
                    recordChangeFilterAction();

                    const durationRange = getRange({
                      activeThumb,
                      values,
                      max: leg.durations.max,
                      min: leg.durations.min,
                    });

                    if (durationRange) handleDurationChangeCommitted(durationRange);
                  }}
                />
              </div>
            </React.Fragment>
          );
        })}

        <div className={styles.itemHeader}>{translations.stopover}</div>
        {stopoverDurationFilterOptions ? (
          <div className={styles.sliderContainer} data-testid='slider-stopover'>
            <div className={styles.labelRange}>
              <div className={styles.label}>
                {durationDisplayFormat(selectedStopoverDurationForActiveLeg[0])}
              </div>
              <div className={styles.label}>
                {durationDisplayFormat(selectedStopoverDurationForActiveLeg[1])}
              </div>
            </div>

            <Slider
              dataPw='flightFilter_duration'
              isRtl={isRtl}
              max={stopoverDurationFilterOptions.max}
              min={stopoverDurationFilterOptions.min}
              step={5}
              value={selectedStopoverDurationForActiveLeg}
              onChange={(values, activeThumb) => {
                const durationRange = getRange({
                  activeThumb,
                  values,
                  max: stopoverDurationFilterOptions.max,
                  min: stopoverDurationFilterOptions.min,
                });

                if (durationRange) handleActiveLegStopoverDurationChanged(durationRange);
              }}
              onChangeCommitted={(values, activeThumb) => {
                recordChangeFilterAction();

                const durationRange = getRange({
                  activeThumb,
                  values,
                  max: stopoverDurationFilterOptions.max,
                  min: stopoverDurationFilterOptions.min,
                });

                if (durationRange) handleSelectedStopoverDurationChanged(durationRange);
              }}
            />
          </div>
        ) : null}
      </div>
    );

  return (
    <div
      className={clsx(
        styles.categoryContent,
        !!className && styles.inDropdown,
        !!className && styles.durationContainer,
      )}
    >
      <div className={styles.itemHeader}>
        {translations.trip_time}{' '}
        <span className={styles.route}>
          {renderLegLabel({
            depart:
              legFilterOptions[activeLeg].departureAirport?.code ||
              legFilterOptions[activeLeg].departureCity?.code ||
              '',
            arrival:
              legFilterOptions[activeLeg].arrivalAirport?.code ||
              legFilterOptions[activeLeg].arrivalCity?.code ||
              '',
            isRtl,
          })}
        </span>
      </div>
      <div className={clsx(styles.sliderContainer)}>
        <div className={styles.labelRange}>
          <div className={styles.label}>
            {durationDisplayFormat(selectedDepartDurationForActiveLeg[0])}
          </div>
          <div className={styles.label}>
            {durationDisplayFormat(selectedDepartDurationForActiveLeg[1])}
          </div>
        </div>

        <Slider
          dataPw='flightFilter_duration'
          isRtl={isRtl}
          max={legFilterOptions[activeLeg].leg!.durations.max}
          min={legFilterOptions[activeLeg].leg!.durations.min}
          step={5}
          value={selectedDepartDurationForActiveLeg}
          onChange={(values, activeThumb) => {
            const durationRange = getRange({
              activeThumb,
              values,
              max: legFilterOptions[activeLeg].leg!.durations.max,
              min: legFilterOptions[activeLeg].leg!.durations.min,
              minimumDistance: 5,
            });

            if (durationRange) handleActiveLegDepartDurationChanged(durationRange);
          }}
          onChangeCommitted={(values, activeThumb) => {
            recordChangeFilterAction();

            const durationRange = getRange({
              activeThumb,
              values,
              max: legFilterOptions[activeLeg].leg!.durations.max,
              min: legFilterOptions[activeLeg].leg!.durations.min,
            });

            if (durationRange) handleSelectedDepartDurationChanged(durationRange);
          }}
        />
      </div>

      <div className={styles.itemHeader}>
        {className ? translations.transit_duration : translations.stopover}
      </div>
      {stopoverDurationFilterOptions ? (
        <div className={styles.sliderContainer}>
          <div className={styles.labelRange}>
            <div className={styles.label}>
              {durationDisplayFormat(selectedStopoverDurationForActiveLeg[0])}
            </div>
            <div className={styles.label}>
              {durationDisplayFormat(selectedStopoverDurationForActiveLeg[1])}
            </div>
          </div>

          <Slider
            dataPw='flightFilter_duration'
            isRtl={isRtl}
            max={stopoverDurationFilterOptions.max}
            min={stopoverDurationFilterOptions.min}
            step={5}
            value={selectedStopoverDurationForActiveLeg}
            onChange={(values, activeThumb) => {
              const durationRange = getRange({
                activeThumb,
                values,
                max: stopoverDurationFilterOptions.max,
                min: stopoverDurationFilterOptions.min,
              });

              if (durationRange) handleActiveLegStopoverDurationChanged(durationRange);
            }}
            onChangeCommitted={(values, activeThumb) => {
              recordChangeFilterAction();

              const durationRange = getRange({
                activeThumb,
                values,
                max: stopoverDurationFilterOptions.max,
                min: stopoverDurationFilterOptions.min,
              });

              if (durationRange) handleSelectedStopoverDurationChanged(durationRange);
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

export default DurationFilter;
